import { z } from "zod";

// ========== Const enums ==========

export const WorkflowTriggerType = {
	entityCreated: "entity_created",
	statusEntered: "status_entered",
	statusExited: "status_exited",
} as const;
export type WorkflowTriggerTypeValue =
	(typeof WorkflowTriggerType)[keyof typeof WorkflowTriggerType];

export const WorkflowActionType = {
	setStatus: "set_status",
	setAssignee: "set_assignee",
	sendEmail: "send_email",
	createEntity: "create_entity",
} as const;
export type WorkflowActionTypeValue =
	(typeof WorkflowActionType)[keyof typeof WorkflowActionType];

export const WorkflowConditionOperator = {
	equals: "eq",
	notEquals: "ne",
	greaterThan: "gt",
	lessThan: "lt",
	greaterThanOrEqual: "gte",
	lessThanOrEqual: "lte",
	isEmpty: "isnull",
	isNotEmpty: "isnotnull",
} as const;
export type WorkflowConditionOperatorValue =
	(typeof WorkflowConditionOperator)[keyof typeof WorkflowConditionOperator];

export const WorkflowEntityType = {
	jobType: "job",
	ticketType: "ticket",
	recordType: "record",
	assetType: "asset",
} as const;
export type WorkflowEntityTypeValue =
	(typeof WorkflowEntityType)[keyof typeof WorkflowEntityType];

// ========== Scope schemas ==========

export const WorkflowRuleScopeDisplaySchema = z.object({
	subOrganizationId: z.string().nullish(),
	jobTypeId: z.string().nullish(),
	assetTypeId: z.string().nullish(),
	ticketTypeId: z.string().nullish(),
	recordTypeId: z.string().nullish(),
	enabled: z.boolean(),
});
export type WorkflowRuleScopeDisplay = z.infer<
	typeof WorkflowRuleScopeDisplaySchema
>;

export const WorkflowRuleScopeInfoSchema = z.object({
	typeId: z.string().nullable().optional(),
	subOrganizationId: z.string().nullable().optional(),
	enabled: z.boolean(),
});
export type WorkflowRuleScopeInfo = z.infer<typeof WorkflowRuleScopeInfoSchema>;

// ========== Sub-object schemas (must precede CreateWorkflowRuleInfoSchema) ==========

/**
 * WorkflowTriggerInfo — trigger sub-object
 */
export const WorkflowTriggerInfoSchema = z.object({
	type: z.string().nullable().optional(),
	config: z.unknown().optional(),
});
export type WorkflowTriggerInfo = z.infer<typeof WorkflowTriggerInfoSchema>;

/**
 * WorkflowActionInfo — single action sub-object
 */
export const WorkflowActionInfoSchema = z.object({
	type: z.string().nullable().optional(),
	config: z.unknown().optional(),
});
export type WorkflowActionInfo = z.infer<typeof WorkflowActionInfoSchema>;

/**
 * WorkflowCondition — individual condition
 */
export const WorkflowConditionSchema = z.object({
	columnDefinitionId: z.string().nullable().optional(),
	entityPropertyName: z.string().nullable().optional(),
	operator: z.string(),
	compareToStringValue: z.string().nullable().optional(),
	compareToNumericValue: z.number().nullable().optional(),
});
export type WorkflowCondition = z.infer<typeof WorkflowConditionSchema>;

/**
 * WorkflowConditionGroup — Phase 1: flat "All" group
 * (backend schema uses recursive WorkflowConditionGroup2 for subGroups)
 */
export type WorkflowConditionGroup = {
	groupType: string;
	conditions?: WorkflowCondition[];
	subGroups?: WorkflowConditionGroup[];
};
export const WorkflowConditionGroupSchema: z.ZodType<WorkflowConditionGroup> =
	z.lazy(() =>
		z.object({
			groupType: z.string(),
			conditions: z.array(WorkflowConditionSchema).optional(),
			subGroups: z.array(WorkflowConditionGroupSchema).optional(),
		}),
	);

// ========== Request schemas ==========

/**
 * POST /workflows/workflow-rules
 * Includes trigger, actions, and condition directly — no separate version POST needed.
 */
export const CreateWorkflowRuleInfoSchema = z.object({
	id: z.string().nullable().optional(),
	organizationId: z.string().nullable().optional(),
	name: z.string(),
	description: z.string().nullable().optional(),
	entityType: z.string(),
	subOrganizationIds: z.array(z.string()).nullable().optional(),
	jobTypeId: z.string().nullable().optional(),
	ticketTypeId: z.string().nullable().optional(),
	assetTypeId: z.string().nullable().optional(),
	recordTypeId: z.string().nullable().optional(),
	trigger: WorkflowTriggerInfoSchema.nullable().optional(),
	actions: z.array(WorkflowActionInfoSchema).nullable().optional(),
	condition: WorkflowConditionGroupSchema.nullable().optional(),
	minConditionMetCount: z.number().int().optional(),
	maxActionFiredCount: z.number().int().optional(),
});
export type CreateWorkflowRuleInfo = z.infer<
	typeof CreateWorkflowRuleInfoSchema
>;

/**
 * PUT /workflows/workflow-rules/{ruleId}/versions/{versionId}
 * Kept for future use (e.g. editing an existing draft version).
 */
export const EditWorkflowRuleVersionInfoSchema = z.object({
	organizationId: z.string().nullable().optional(),
	trigger: WorkflowTriggerInfoSchema.optional(),
	actions: z.array(WorkflowActionInfoSchema).nullable().optional(),
	condition: WorkflowConditionGroupSchema.nullable().optional(),
	minConditionMetCount: z.number().int().optional(),
	maxActionFiredCount: z.number().int().optional(),
});
export type EditWorkflowRuleVersionInfo = z.infer<
	typeof EditWorkflowRuleVersionInfoSchema
>;

// ========== Response schemas ==========

/**
 * WorkflowTriggerDisplay — trigger object in rule version responses
 */
export const WorkflowTriggerDisplaySchema = z.object({
	type: z.string().nullish(),
	config: z.unknown().nullish(),
});
export type WorkflowTriggerDisplay = z.infer<
	typeof WorkflowTriggerDisplaySchema
>;

/**
 * WorkflowActionDisplay — single action in rule version responses
 */
export const WorkflowActionDisplaySchema = z.object({
	type: z.string().nullish(),
	config: z.unknown().nullish(),
});
export type WorkflowActionDisplay = z.infer<typeof WorkflowActionDisplaySchema>;

/**
 * WorkflowConditionDisplay — single condition in a condition group
 */
export const WorkflowConditionDisplaySchema = z.object({
	columnDefinitionId: z.string().nullish(),
	entityPropertyName: z.string().nullish(),
	operator: z.string().nullish(),
	compareToColumnDefinitionId: z.string().nullish(),
	compareToEntityPropertyName: z.string().nullish(),
	compareToStringValue: z.string().nullish(),
	compareToNumericValue: z.number().nullish(),
});
export type WorkflowConditionDisplay = z.infer<
	typeof WorkflowConditionDisplaySchema
>;

/**
 * WorkflowConditionGroupDisplay — recursive condition group in rule version responses
 */
export type WorkflowConditionGroupDisplay = {
	groupType?: string | null;
	conditions?: WorkflowConditionDisplay[] | null;
	subGroups?: WorkflowConditionGroupDisplay[] | null;
};
export const WorkflowConditionGroupDisplaySchema: z.ZodType<WorkflowConditionGroupDisplay> =
	z.lazy(() =>
		z.object({
			groupType: z.string().nullish(),
			conditions: z.array(WorkflowConditionDisplaySchema).nullish(),
			subGroups: z.array(WorkflowConditionGroupDisplaySchema).nullish(),
		}),
	);

/**
 * WorkflowRuleVersionSummaryDisplay — returned inside WorkflowRuleDisplay.
 * Expanded to include the full trigger/actions/condition payload as per swagger.
 */
export const WorkflowRuleVersionSummaryDisplaySchema = z.object({
	id: z.string().uuid(),
	version: z.number().int(),
	active: z.boolean(),
	minConditionMetCount: z.number().int().nullish(),
	maxActionFiredCount: z.number().int().nullish(),
	trigger: WorkflowTriggerDisplaySchema.nullish(),
	actions: z.array(WorkflowActionDisplaySchema).nullish(),
	condition: WorkflowConditionGroupDisplaySchema.nullish(),
});
export type WorkflowRuleVersionSummaryDisplay = z.infer<
	typeof WorkflowRuleVersionSummaryDisplaySchema
>;

/**
 * WorkflowRuleDisplay — returned by GET /workflows/workflow-rules/{id}
 * and array items from GET /workflows/workflow-rules/{start}/{count}
 */
export const WorkflowRuleDisplaySchema = z.object({
	id: z.string().uuid(),
	organizationId: z.string().uuid().nullish(),
	name: z.string().nullish(),
	description: z.string().nullish(),
	entityType: z.string(),
	status: z.string(),
	latestVersion: WorkflowRuleVersionSummaryDisplaySchema.nullish(),
	activeVersion: WorkflowRuleVersionSummaryDisplaySchema.nullish(),
	scopes: z.array(WorkflowRuleScopeDisplaySchema).nullish(),
});
export type WorkflowRuleDisplay = z.infer<typeof WorkflowRuleDisplaySchema>;

// ========== List params ==========

/**
 * Query params for GET /workflows/workflow-rules/{start}/{count}
 * and GET /workflows/workflow-rules/count
 */
export type WorkflowRulesParams = {
	SearchTerm?: string;
	EntityType?: WorkflowEntityTypeValue;
	EntityTypeId?: string;
	Status?: "draft" | "active" | "archived";
	OrganizationId?: string;
	SubOrganizationId?: string;
	ForOrganizationUser?: string;
	CreatedBefore?: string;
	CreatedAfter?: string;
	UpdatedBefore?: string;
	UpdatedAfter?: string;
	"OrderBy.Fields"?: string[];
	"OrderBy.Descending"?: boolean;
};

// ========== ConditionOperatorsByDataType ==========

/**
 * GET /workflows/condition-operators-by-data-type
 * Maps a data type (e.g. "string", "dateTime", "number") to its allowed operators.
 */
export const WorkflowConditionOperatorsByDataTypeSchema = z.object({
	dataType: z.string(),
	operators: z.array(z.string()),
});
export type WorkflowConditionOperatorsByDataType = z.infer<
	typeof WorkflowConditionOperatorsByDataTypeSchema
>;

// ========== ServiceResult ==========

export const WorkflowServiceResultSchema = z
	.object({
		success: z.boolean().optional(),
		isSuccess: z.boolean().optional(),
		message: z.string().nullable().optional(),
		errorMessages: z.array(z.string()).nullable().optional(),
		affectedId: z.string().nullable().optional(),
	})
	.transform((raw) => ({
		success: raw.success ?? raw.isSuccess ?? false,
		message: raw.message ?? null,
		affectedId: raw.affectedId ?? null,
	}));
export type WorkflowServiceResult = z.output<
	typeof WorkflowServiceResultSchema
>;
